/**
 * Cart drawer — custom element + Vue app.
 *
 * The element is named <cart-drawer> and exposes .open() on purpose: Shopify's
 * standard-actions runtime queries for `cart-drawer-component` then `cart-drawer` and calls
 * `.open()` on whichever it finds, falling back to navigating to /cart. Matching that
 * contract means `Shopify.actions.openCart()` — and so apps, pixels and AI shopping agents —
 * drive this drawer with no registration code.
 *
 * No module system in assets/ — plain script, Vue from the global build.
 */
(function () {
  const CONFIG_ID = 'cart-drawer-config';
  const APP_ID = 'cart-drawer-app';

  const readConfig = () => {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[cart-drawer] config failed to parse', e);
      return null;
    }
  };

  /**
   * Renders cents using the shop's money format so the drawer matches Liquid's `money`
   * filter rather than inventing its own currency formatting.
   */
  const makeMoneyFormatter = (moneyFormat) => (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (moneyFormat && moneyFormat.indexOf('{{') !== -1) {
      return moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, withCommas);
    }
    return `$${withCommas}`;
  };

  /**
   * Requests a CDN-resized copy of an image.
   *
   * Embroidery previews are uploaded at 1500x1500 (~102KB) but the lightbox never renders one
   * larger than PREVIEW_WIDTH, so fetching the original made opening the preview feel slow. The
   * cart property holds a raw Files URL with no size applied, hence doing it here.
   *
   * Only Shopify CDN URLs are rewritten — the param means nothing on another host, and blindly
   * appending to a signed third-party URL could invalidate it.
   */
  const PREVIEW_WIDTH = 750;
  const LINE_IMAGE_WIDTH = 300;
  const sizedImageUrl = (url, width) => {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
    if (/[?&]width=/.test(url)) return url;
    return `${url}${url.indexOf('?') === -1 ? '?' : '&'}width=${width}`;
  };

  /**
   * Klarna is loaded on demand. Its SDK is not present on non-cart pages, and the drawer
   * opens everywhere, so loading it up front would add a third-party script to every page.
   * Guarded so we never double-inject over the app block's own copy on /cart.
   */
  const loadKlarna = (clientId) => {
    if (window.__brightonKlarnaPromise) return window.__brightonKlarnaPromise;

    window.__brightonKlarnaPromise = new Promise((resolve, reject) => {
      const whenReady = () => {
        if (window.Klarna && window.Klarna.OnsiteMessaging) return resolve(window.Klarna);
        const started = Date.now();
        const poll = setInterval(() => {
          if (window.Klarna && window.Klarna.OnsiteMessaging) {
            clearInterval(poll);
            resolve(window.Klarna);
          } else if (Date.now() - started > 8000) {
            clearInterval(poll);
            reject(new Error('Klarna SDK did not initialise'));
          }
        }, 100);
        return undefined;
      };

      if (window.Klarna && window.Klarna.OnsiteMessaging) return resolve(window.Klarna);
      // The app block injected it; wait for it rather than adding a second tag.
      if (document.getElementById('klarna-osm-script-tag')) return whenReady();

      const script = document.createElement('script');
      script.id = 'klarna-osm-script-tag';
      script.async = true;
      script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
      script.setAttribute('data-environment', 'production');
      script.setAttribute('data-client-id', clientId);
      // Not data-client-instance-name despite the duplicate-SDK warning: a named instance
      // exposes no OnsiteMessaging anywhere, so refresh() is unreachable and the block is blank.
      script.onload = whenReady;
      script.onerror = () => reject(new Error('Klarna SDK failed to load'));
      document.head.appendChild(script);
      return undefined;
    });

    return window.__brightonKlarnaPromise;
  };

  const bootstrap = () => {
    const config = readConfig();
    const mount = document.getElementById(APP_ID);
    const host = document.querySelector('cart-drawer');
    if (!config || !config.enabled) return;
    if (!mount || !host || !window.Vue || !window.BrightonCartStore) return;

    const { createApp, computed, reactive, watch, nextTick } = window.Vue;

    // Declared ahead of the app so the methods closing over them are never in their TDZ.
    let quickViewReturnFocus = null;
    let wrapReturnFocus = null;

    // Mirrors the nth-of-type rules in the CSS, so the +N count stays truthful.
    const SWATCH_LIMIT_WIDE = 4;
    const SWATCH_LIMIT_NARROW = 3;
    const narrowSwatches = window.matchMedia('(max-width: 419px)');
    const currentSwatchLimit = () => (narrowSwatches.matches ? SWATCH_LIMIT_NARROW : SWATCH_LIMIT_WIDE);

    const DETAIL_ROWS_COLLAPSED = 2;

    const decodeEntities = (value) => {
      if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
      const el = document.createElement('textarea');
      el.innerHTML = value;
      return el.value;
    };

    // Threshold copy must stay on one line; CSS can't fit text to a width, so measure and scale.
    const MIN_MESSAGE_PX = 11;
    const fitOneLine = (el) => {
      if (!el) return;
      // Clear any previous adjustment so this measures against the stylesheet's size.
      el.style.fontSize = '';
      const available = el.clientWidth;
      if (!available) return;
      const needed = el.scrollWidth;
      if (needed <= available) return;
      const current = parseFloat(window.getComputedStyle(el).fontSize) || 14;
      const scaled = Math.max(MIN_MESSAGE_PX, Math.floor(current * (available / needed)));
      el.style.fontSize = `${scaled}px`;
    };

    const fitThresholdMessages = () => {
      host.querySelectorAll('.cart-drawer__threshold').forEach(fitOneLine);
    };

    /**
     * The block after the item list is pushed to the foot of the drawer, so a short cart leaves
     * a gap between the two. The last line needs its closing border only in that case — when the
     * cart is long enough for the two to meet, the border would sit on the seam.
     *
     * Measured rather than inferred: margin-top:auto silently collapses to zero once the content
     * overflows, and no media query or :has() can report which of the two happened.
     */
    const measureTrailingGap = () => {
      const items = host.querySelector('.cart-drawer__items');
      const trailing = items && items.nextElementSibling;
      if (!items || !trailing) {
        host.classList.remove('cart-drawer--items-detached');
        return;
      }
      const gap = trailing.getBoundingClientRect().top - items.getBoundingClientRect().bottom;
      host.classList.toggle('cart-drawer--items-detached', gap > 1);
    };

    // Built via RegExp from ASCII escape strings, deliberately. U+FE0F (variation selector)
    // and U+200D (zero-width joiner) are invisible in an editor, so writing them literally
    // means a careless edit silently drops them.
    //
    // Using the constructor also makes the fallback reachable: a literal
    // /\p{Extended_Pictographic}/gu is a PARSE-time syntax error on engines without Unicode
    // property escapes, which would break this whole file before any try/catch could run.
    // snippets/wrapin.liquid has that latent bug; this does not.
    const RE_VARIATION_SELECTOR = new RegExp('\\uFE0F', 'g');
    const RE_ZERO_WIDTH_JOINER = new RegExp('\\u200D', 'g');
    const RE_PICTOGRAPHIC = (() => {
      try {
        return new RegExp('\\p{Extended_Pictographic}', 'gu');
      } catch (e) {
        return null;
      }
    })();
    const RE_EMOJI_FALLBACK = new RegExp(
      '[\\u203C-\\u3299\\u2600-\\u26FF\\u2700-\\u27BF]'
        + '|\\uD83C[\\uDC00-\\uDFFF]|\\uD83D[\\uDC00-\\uDFFF]|\\uD83E[\\uDD00-\\uDDFF]',
      'g'
    );

    /**
     * Strips emoji from a gift message. Ported from snippets/wrapin.liquid — the wrap
     * fulfilment pipeline cannot print them.
     */
    const removeEmojis = (str) => {
      if (!str) return str;
      return str
        .replace(RE_PICTOGRAPHIC || RE_EMOJI_FALLBACK, '')
        .replace(RE_VARIATION_SELECTOR, '')
        .replace(RE_ZERO_WIDTH_JOINER, '');
    };
    const store = window.BrightonCartStore(window.Vue);
    const formatMoney = makeMoneyFormatter(config.moneyFormat);

    const app = createApp({
      setup() {
        const state = store.state;
        const cart = computed(() => state.cart);

        /**
         * Per-line UI state that isn't cart data: which detail accordions are open, which values
         * were just copied, and enrichment results keyed by line key.
         */
        const ui = reactive({
          /** Swatches per recommendation card before +N truncates. Viewport-dependent. */
          swatchLimit: currentSwatchLimit(),
          /** The add-on whose quick view panel is open, or null. */
          quickViewAddOn: null,
          quickViewQuantity: 1,
          /** lineKey -> true once the detail accordion is opened. */
          expandedDetails: {},
          copiedDesignIds: {},
          /** { url, title } while the embroidery preview lightbox is open. */
          preview: null,
          embroidery: {},
          /**
           * Gift wrap is edited as a LOCAL DRAFT and only written to the cart on confirm.
           * Writing on every interaction meant opening the panel immediately added a variant
           * and each print click swapped it — a cart mutation per click, before the customer
           * had decided anything or typed a message.
           */
          wrapPanelOpen: false,
          /** Line key the wrap panel is editing in product mode; null means order-level. */
          wrapLineKey: null,
          draftPrintId: null,
          giftMessage: null,
          /** null until the customer touches it, so it keeps tracking the cart attribute. */
          wrapIndividual: null,
          emojiStripped: false,
          /** Recommendation products, from Nosto or the manual list. */
          recommendations: [],
          recommendationsTitle: '',
          /** Nosto's result_id, needed for click and add-to-cart attribution. */
          recommendationsResultId: '',
          /** True when Nosto answered with a fallback rather than a real recommendation. */
          recommendationsIsFallback: false,
          /** productId -> { optionName: value }, so each card remembers its selections. */
          recOptionChoice: {},
          /** productId -> true once the customer expands a truncated swatch row. */
          recSwatchesExpanded: {},
          recommendationsOverflow: false,
          /** Variant ids already in the Swym "Save For Later" list. */
          savedVariantIds: [],
          /** Line keys with an in-flight save, so the control can show progress. */
          savingLines: {},
        });

        const visibleItems = computed(() => state.cart.items.filter((i) => !i.flags.hidden));
        const hasItems = computed(() => visibleItems.value.length > 0);

        /**
         * Tag-triggered add-ons. A slot qualifies while the cart holds a product carrying one of
         * its trigger tags, and drops out once the add-on itself is in the cart — so the row is
         * never offering something the customer already has.
         *
         * Matched against each line's `hookClasses`, which already lists every product tag in
         * handleized `tag-` form, rather than shipping the raw tag arrays a second time.
         *
         * Hidden lines are deliberately included as triggers: a checkout-injected gift is still a
         * product in the cart, and the cart page's own loop counts it too.
         */
        const addOnConfig = config.addOns || { enabled: false, items: [] };
        const visibleAddOns = computed(() => {
          if (!addOnConfig.enabled || !addOnConfig.items.length) return [];

          const items = state.cart.items;
          if (!items.length) return [];

          const inCart = new Set(items.map((i) => i.productId));
          const tagClasses = new Set();
          items.forEach((i) => {
            String(i.hookClasses || '').split(' ').forEach((cls) => {
              if (cls) tagClasses.add(cls);
            });
          });

          return addOnConfig.items.filter(
            (addOn) => !inCart.has(addOn.productId)
              && (addOn.triggerClasses || []).some((cls) => tagClasses.has(cls))
          );
        });
        const showAddOns = computed(() => visibleAddOns.value.length > 0);

        const unlockedGifts = computed(() =>
          state.cart.gwp.slots.filter((s) => s.unlocked && s.gift)
        );

        const shippingTarget = computed(() => config.shipping.thresholdCents || 0);
        const shippingMet = computed(() => shippingTarget.value > 0 && state.cart.totalPrice >= shippingTarget.value);
        const shippingRemaining = computed(() => Math.max(0, shippingTarget.value - state.cart.totalPrice));
        const showShippingBar = computed(() => config.shipping.enabled && shippingTarget.value > 0);

        // The announcement bar is the shipping row, so it only exists once a threshold is set.
        const showShippingRow = showShippingBar;

        const thresholdIcon = (kind, met) => {
          const icons = config.thresholdIcons || {};
          return (met && icons[`${kind}Met`]) || icons[kind] || null;
        };

        const shippingRow = computed(() => ({
          met: shippingMet.value,
          message: shippingMet.value
            ? config.shipping.metMessage
            : `${config.shipping.notMetPrefix}${formatMoney(shippingRemaining.value)}${config.shipping.notMetSuffix}`,
        }));

        /**
         * One message template for all three tiers. A tier with no gift, or one switched off by
         * an unreachable threshold, contributes no row — Brighton disables a gift by leaving it
         * enabled at $1,000,000, which must never reach a customer as copy.
         */
        const fillTokens = (template, slot) =>
          String(template || '')
            .split('{threshold}').join(formatMoney(slot.thresholdCents))
            .split('{remaining}').join(formatMoney(slot.remainingCents))
            .split('{gift}').join(slot.gift.title);

        const gwpRows = computed(() => {
          const notMet = config.gwp.notMet;
          const met = config.gwp.met;
          if (!notMet && !met) return [];
          return (state.cart.gwp.slots || [])
            .filter((s) => s.enabled && s.gift && s.thresholdCents > 0)
            .sort((a, b) => a.thresholdCents - b.thresholdCents)
            .map((s) => {
              const template = s.unlocked ? met : notMet;
              if (!template) return null;
              return {
                key: `gwp-${s.slot}`,
                met: !!s.unlocked,
                prefix: fillTokens(config.gwp.prefix, s),
                message: fillTokens(template, s),
              };
            })
            .filter(Boolean)
            // Earned tiers, then only the next one to aim for — the tier after that stays
            // hidden until this one is earned.
            .filter((row, i, all) => row.met || all.findIndex((r) => !r.met) === i);
        });
        // Narrows only when EVERY wrappable line is a handbag; a mixed cart offers all prints.
        const handbagOnly = computed(() => {
          const hook = config.giftWrap.handbagClass;
          if (!hook || !config.giftWrap.handbagPrint) return false;
          const lines = (state.cart.items || []).filter((i) => !i.flags.hidden && i.flags.wrapEligible);
          return lines.length > 0 && lines.every((i) => (i.hookClasses || []).indexOf(hook) !== -1);
        });

        const hasHandbag = computed(() => {
          const hook = config.giftWrap.handbagClass;
          if (!hook) return false;
          return (state.cart.items || []).some(
            (i) => !i.flags.hidden && i.flags.wrapEligible && (i.hookClasses || []).indexOf(hook) !== -1
          );
        });

        // Without the tag configured there is no way to tell a handbag apart, so the note keeps showing.
        const showWrapNote = computed(() =>
          !!config.giftWrap.note && (!config.giftWrap.handbagClass || hasHandbag.value)
        );

        const showTogetherMessage = computed(() =>
          !!config.giftWrap.togetherMessage &&
          !config.giftWrap.askIndividual &&
          !(config.giftWrap.enabled && config.giftWrap.mode === 'product')
        );

        const availablePrints = computed(() => {
          const prints = (state.cart.giftWrap && state.cart.giftWrap.prints) || [];
          if (!handbagOnly.value) return prints;
          const allowed = prints.filter((p) => p.title === config.giftWrap.handbagPrint);
          // Fail open on a mistyped title rather than rendering an empty grid.
          return allowed.length ? allowed : prints;
        });

        /**
         * Enriches every embroidery line that hasn't been enriched yet. Runs on cart change,
         * which means the work happens at add-to-cart time rather than on drawer open — the
         * Design ID is already cached by the time the customer looks at it.
         */
        const enrichEmbroidery = () => {
          const api = window.BrightonDrawerEmbroidery;
          if (!api) return;
          state.cart.items.forEach((item) => {
            if (!item.embroidery || ui.embroidery[item.key]) return;
            // Rows are derived locally, so show them immediately; the ID fills in after.
            ui.embroidery[item.key] = { rows: api.buildRows(item.embroidery.rawProperties), designId: null };
            api.enrich(item).then((result) => {
              if (result) ui.embroidery[item.key] = result;
            });
          });
        };

        watch(() => state.cart.items.map((i) => i.key).join('|'), enrichEmbroidery, { immediate: true });

        /**
         * Swym's list is fetched once Swym is ready, then again after each save, so the control
         * disappears for items the customer has already saved.
         */
        const refreshSavedVariantIds = () => {
          const api = window.BrightonDrawerSwym;
          if (!api || !config.saveForLater.enabled) return;
          api.savedVariantIds()
            .then((ids) => { ui.savedVariantIds = ids; })
            .catch(() => { /* Swym unavailable — the control simply stays offered */ });
        };
        refreshSavedVariantIds();

        /**
         * Loads recommendations. Nosto is tried first when selected, falling back to the manual
         * list if it doesn't initialise — which is always the case on the dev server.
         *
         * The cart is pushed to Nosto BEFORE requesting, otherwise a cart placement has no cart
         * context and Nosto answers with a generic fallback instead of a recommendation.
         */
        const loadRecommendations = async () => {
          const cfg = config.recommendations;
          if (!cfg.enabled) return;

          const useManual = () => {
            ui.recommendations = cfg.manualProducts || [];
            ui.recommendationsTitle = cfg.title;
            ui.recommendationsResultId = '';
            ui.recommendationsIsFallback = false;
          };

          if (cfg.source !== 'nosto') return useManual();

          const api = window.BrightonDrawerRecommendations;
          if (!api) return useManual();

          await api.syncCartToNosto(state.cart);
          const result = await api.fetchNosto(cfg.nostoPlacementId);
          if (!result || !result.products.length) return useManual();

          ui.recommendations = result.products;
          ui.recommendationsTitle = cfg.useNostoTitle && result.title ? result.title : cfg.title;
          ui.recommendationsResultId = result.resultId;
          // Surfaced so a fallback result is visible in QA rather than looking like a real
          // personalised response.
          ui.recommendationsIsFallback = api.isFallbackResult(result.resultId);
          if (ui.recommendationsIsFallback) {
            console.info(
              `[cart-drawer] Nosto returned a FALLBACK for "${cfg.nostoPlacementId}" (${result.resultId}) — not cart-personalised.`
            );
          }
          return undefined;
        };

        /**
         * Re-requests when the cart changes, so recommendations actually reflect the cart.
         * Debounced because quantity steppers fire in bursts and each reload is a Nosto request.
         */
        let recTimer = null;
        const queueRecommendationRefresh = () => {
          if (recTimer) clearTimeout(recTimer);
          recTimer = setTimeout(() => { loadRecommendations(); }, 600);
        };

        watch(
          () => state.cart.items.map((i) => `${i.variantId}:${i.quantity}`).join('|'),
          queueRecommendationRefresh
        );

        loadRecommendations();

        const showRecommendations = computed(
          () => config.recommendations.enabled && visibleRecommendations.value.length > 0
        );

        /**
         * Nosto's result_id when Nosto supplied the list, otherwise the manual source — so
         * reporting can tell a personalised list from a merchandised one.
         */
        const recommendationsListId = computed(
          () => ui.recommendationsResultId || `manual:${config.recommendations.nostoPlacementId || 'cart'}`
        );

        /**
         * One impression event per distinct list, fired when the carousel is actually on screen
         * rather than merely rendered — the drawer's body scrolls, so a rendered carousel is
         * often below the fold.
         */
        let impressionKey = null;
        const announceImpression = () => {
          if (!showRecommendations.value) return;
          const products = visibleRecommendations.value;
          const key = `${recommendationsListId.value}|${products.map((p) => p.productId).join(',')}`;
          if (key === impressionKey) return;
          impressionKey = key;
          document.dispatchEvent(new CustomEvent('brighton:cart:recommendations-view', {
            detail: {
              products,
              listId: recommendationsListId.value,
              listName: ui.recommendationsTitle,
            },
          }));
        };

        /**
         * Products already in the cart are filtered out, then the list is trimmed. That's the
         * substitution behaviour: picking more than are shown means the extras step in when
         * earlier ones are already in the cart.
         */
        const visibleRecommendations = computed(() => {
          const inCart = new Set(state.cart.items.map((i) => String(i.productId)));
          return ui.recommendations.filter((r) => !inCart.has(String(r.productId)));
        });

        const carouselStyle = computed(() => ({
          '--cart-drawer-rec-visible': String(config.recommendations.visibleCount || 3),
        }));

        /** Gift wrap is offered whenever it's enabled, configured, and the cart isn't empty. */
        // Order-level control only; product mode puts the affordance on each line instead.
        const showGiftWrap = computed(
          () => config.giftWrap.enabled
            && config.giftWrap.mode !== 'product'
            && state.cart.giftWrap.prints.length > 0
            && visibleItems.value.length > 0
        );

        /** Which print is actually in the cart — derived from the wrap line. */
        const appliedPrintId = computed(() => {
          const wrapLine = state.cart.items.find((i) => i.flags.isWrap);
          return wrapLine ? wrapLine.variantId : null;
        });


        /**
         * The draft wins while the customer is typing; otherwise fall back to the server's
         * attribute so the field is correct on open and after an external change.
         */
        const giftMessage = computed(() =>
          ui.giftMessage !== null ? ui.giftMessage : state.cart.giftWrap.message || ''
        );

        const productWrapMode = computed(() => config.giftWrap.enabled && config.giftWrap.mode === 'product');
        const wrapLineItem = computed(() =>
          state.cart.items.find((i) => i.key === ui.wrapLineKey) || null
        );

        // Always starts unchecked; only the customer's own tick in this session turns it on.
        const wrapIndividual = computed(() => ui.wrapIndividual === true);

        /**
         * Wrap costs across every wrapped line. Product mode adds no line item, so this never
         * reaches cart.total_price — the Cart Transform function creates the charge at checkout.
         * Disclosed separately so the drawer total doesn't jump on the checkout page.
         */
        const wrapTotal = computed(() => {
          if (config.giftWrap.mode !== 'product') return 0;

          const keys = config.giftWrap.propertyKeys;
          const prints = state.cart.giftWrap.prints;

          return state.cart.items.reduce((sum, item) => {
            const props = item.allProperties || {};
            if (props[keys.added] !== config.giftWrap.addedValue) return sum;

            const print = prints.find((p) => String(p.variantId) === String(props[keys.printId]));
            return sum + ((print && print.price) || 0) * item.quantity;
          }, 0);
        });

        const giftMessageRemaining = computed(() =>
          Math.max(0, config.giftWrap.messageMaxLength - giftMessage.value.length)
        );

        // A removed or blocked wrap clears the draft, so reopening doesn't resurrect it.
        watch(
          () => state.cart.giftWrap.inCart,
          (inCart) => {
            if (!inCart) {
              ui.giftMessage = null;
              ui.draftPrintId = null;
              ui.emojiStripped = false;
            }
          }
        );
        // Registered last on purpose: watch() evaluates its source once at creation to collect
        // dependencies, so naming a computed declared further down throws on mount.
        watch(
          () => [state.cart.items.length, showAddOns.value, showRecommendations.value].join('|'),
          () => { nextTick(measureTrailingGap); }
        );

        return {
          config,
          cart,
          showRecommendations,
          visibleRecommendations,
          recommendationsListId,
          announceImpression,
          recommendationsTitle: computed(() => ui.recommendationsTitle),
          recommendationsOverflow: computed(
            () => visibleRecommendations.value.length > (config.recommendations.visibleCount || 3)
          ),
          carouselStyle,
          swatchLimit: computed(() => ui.swatchLimit),
          visibleAddOns,
          showAddOns,
          showGiftWrap,
          appliedPrintId,
          wrapPanelOpen: computed(() => ui.wrapPanelOpen),
          draftPrintId: computed(() => ui.draftPrintId),
          giftMessage,
          giftMessageRemaining,
          wrapIndividual,
          productWrapMode,
          wrapLineItem,
          wrapTotal,
          collapsedDetailRows: DETAIL_ROWS_COLLAPSED,
          emojiStripped: computed(() => ui.emojiStripped),
          uiState: ui,
          preview: computed(() => ui.preview),
          savedVariantIds: computed(() => ui.savedVariantIds),
          refreshSavedVariantIds,
          copiedDesignIds: ui.copiedDesignIds,
          embroideryState: ui.embroidery,
          isOpen: computed(() => state.open),
          liveMessage: computed(() => state.liveMessage),
          visibleItems,
          hasItems,
          unlockedGifts,
          shippingMet,
          shippingRemaining,
          showShippingBar,
          showShippingRow,
          shippingRow,
          gwpRows,
          thresholdIcon,
          handbagOnly,
          hasHandbag,
          showWrapNote,
          showTogetherMessage,
          availablePrints,
          formatMoney,
        };
      },

      computed: {
        /**
         * Flattened to the same shape as a recommendation card, so chosenOption, chooseOption,
         * isOptionValueAvailable, swatchImageFor, selectedVariantFor and ratingStyle all work on it
         * unchanged.
         */
        quickView() {
          const addOn = this.uiState.quickViewAddOn;
          if (!addOn || !addOn.quickView) return null;
          const qv = addOn.quickView;
          return {
            productId: addOn.productId,
            title: addOn.title,
            url: addOn.url,
            priceText: addOn.priceText,
            properties: addOn.properties,
            options: qv.options || [],
            variants: qv.variants || [],
            images: qv.images || [],
            ratingValue: qv.ratingValue || 0,
            ratingCount: qv.ratingCount || 0,
            finalSale: !!qv.finalSale,
          };
        },

        quickViewQuantity() {
          return this.uiState.quickViewQuantity;
        },

        /** The variant object (not just its id) for the current option choices. */
        quickViewSelected() {
          const product = this.quickView;
          if (!product) return null;
          if (!product.options.length) {
            return product.variants.find((v) => v.available) || product.variants[0] || null;
          }
          const wanted = {};
          product.options.forEach((o) => { wanted[o.name] = this.chosenOption(product, o.name); });
          return product.variants.find(
            (v) => Object.keys(wanted).every((k) => v.optionValues[k] === wanted[k])
          ) || null;
        },

        /**
         * Only the selected colour's photos. Brighton names images
         * `<handle>__<colour-slug>_<n>.jpg`, the same convention imageForColour matches on in
         * cart-drawer-recommendations.js. Falls back to the whole set rather than showing an empty
         * gallery when a product doesn't follow it.
         */
        quickViewImages() {
          const product = this.quickView;
          if (!product) return [];
          const all = product.images || [];
          const colourOption = product.options.find((o) => o.isSwatch);
          if (!colourOption || !all.length) return all;

          const colour = this.chosenOption(product, colourOption.name);
          if (!colour) return all;

          const needle = `__${String(colour).toLowerCase().trim().replace(/\s+/g, '-')}_`;
          const matches = all.filter((src) => String(src).toLowerCase().indexOf(needle) !== -1);
          return matches.length ? matches : all;
        },

        quickViewPriceText() {
          const selected = this.quickViewSelected;
          if (selected && selected.priceText) return selected.priceText;
          return this.quickView ? this.quickView.priceText : '';
        },

        quickViewUrl() {
          const product = this.quickView;
          if (!product) return '';
          const selected = this.quickViewSelected;
          return selected ? `${product.url}?variant=${selected.id}` : product.url;
        },
      },

      watch: {
        // Swym keys the heart to a variant id, so it has to be re-pointed on every option change.
        quickViewSelected() {
          this.syncQuickViewWishlist();
          // The chosen variant's image is prepended, so send the scroll back to it — otherwise the
          // gallery stays where it was and the new first slide is off-screen to the left.
          nextTick(() => {
            const gallery = host.querySelector('.cart-drawer__quick-view-gallery');
            if (gallery) gallery.scrollTo({ left: 0, behavior: 'smooth' });
          });
        },

        /**
         * Dismiss the whole stack with the drawer. Closing via the overlay or the header goes
         * through the store, which knows nothing about these, so they outlived their parent and
         * were left floating over the page.
         */
        isOpen(open) {
          if (open) return;
          this.uiState.quickViewAddOn = null;
          this.uiState.preview = null;
          this.uiState.wrapPanelOpen = false;
          // The drawer restores focus itself; don't fight it on the next quick view close.
          quickViewReturnFocus = null;
        },
      },

      methods: {
        /** Embroidered lines show their rendered preview, so the customer sees what they designed. */
        lineImage(item) {
          const preview = item.embroidery && item.embroidery.previewUrl;
          return preview ? sizedImageUrl(preview, LINE_IMAGE_WIDTH) : item.image;
        },

        /**
         * Cart Transform expands a line into per-unit components, so a line of 3 gets 3 wraps at
         * 3x the price. Returned so the line can say so rather than surprising them at checkout.
         */
        lineWrapPrice(item) {
          const id = Number(this.lineWrapProp(item, 'printId')) || null;
          const print = this.cart.giftWrap.prints.find((p) => p.variantId === id);
          if (!print) return null;
          const unit = print.price || 0;
          return { unit, quantity: item.quantity, total: unit * item.quantity };
        },

        lineWrapPriceText(item) {
          const price = this.lineWrapPrice(item);
          if (!price) return '';
          return price.total > 0 ? this.formatMoney(price.total) : 'Free';
        },

        printPriceText(print) {
          return print.price > 0 ? print.priceText : 'Free';
        },

        lineWrapProp(item, key) {
          const raw = (item.allProperties || {})[config.giftWrap.propertyKeys[key]] || '';
          // Decoded on read only; writeLineWrap leaves every other property untouched.
          return key === 'message' ? decodeEntities(raw) : raw;
        },

        hasLineWrap(item) {
          return this.lineWrapProp(item, 'added') === config.giftWrap.addedValue;
        },

        /** Opens the panel scoped to one line, seeded from whatever that line already carries. */
        openLineWrap(item) {
          const offered = this.availablePrints;
          const existingId = Number(this.lineWrapProp(item, 'printId')) || null;
          const kept = offered.some((p) => p.variantId === existingId) ? existingId : null;
          const first = offered.find((p) => p.available);
          this.uiState.wrapLineKey = item.key;
          this.uiState.draftPrintId = kept || (first ? first.variantId : null);
          this.uiState.giftMessage = this.lineWrapProp(item, 'message');
          this.uiState.emojiStripped = false;
          this.uiState.wrapPanelOpen = true;
          wrapReturnFocus = document.activeElement;
          nextTick(() => {
            if (this.$refs.wrapClose) this.$refs.wrapClose.focus();
          });
        },

        /** Merges onto the line's existing properties — a partial set would strip the rest. */
        async writeLineWrap(item, printId, message) {
          const keys = config.giftWrap.propertyKeys;
          const print = this.cart.giftWrap.prints.find((p) => p.variantId === printId);
          const properties = { ...(item.allProperties || {}) };

          // Cleared with '' rather than deleted: omitting a key does not reliably remove a line
          // property, whereas Shopify drops empty ones.
          if (printId && print) {
            properties[keys.added] = config.giftWrap.addedValue;
            properties[keys.print] = print.title;
            properties[keys.printId] = String(printId);
            properties[keys.message] = message || '';
          } else {
            properties[keys.added] = '';
            properties[keys.print] = '';
            properties[keys.printId] = '';
            properties[keys.message] = '';
          }

          return store.setLineProperties(item.key, properties, 'giftWrap');
        },

        async removeLineWrap(item) {
          await this.writeLineWrap(item, null, '');
          store.announce(`Gift wrap removed from ${item.title}`);
        },

        /** Opens the chooser. Nothing is written to the cart until confirm. */
        openWrapPanel() {
          const offered = this.availablePrints;
          const applied = offered.some((p) => p.variantId === this.appliedPrintId) ? this.appliedPrintId : null;
          const first = offered.find((p) => p.available);
          this.uiState.draftPrintId = applied || (first ? first.variantId : null);
          this.uiState.giftMessage = this.cart.giftWrap.message || '';
          this.uiState.emojiStripped = false;
          this.uiState.wrapPanelOpen = true;
          wrapReturnFocus = document.activeElement;
          nextTick(() => {
            if (this.$refs.wrapClose) this.$refs.wrapClose.focus();
          });
        },

        toggleWrapPanel() {
          if (this.uiState.wrapPanelOpen) {
            this.cancelWrapPanel();
            return;
          }
          this.openWrapPanel();
        },

        /** Discards the draft — the cart is untouched, so there is nothing to roll back. */
        cancelWrapPanel() {
          this.closeWrapPanel();
          const target = wrapReturnFocus;
          wrapReturnFocus = null;
          if (target && typeof target.focus === 'function') nextTick(() => target.focus());
        },

        /** Selection only. No cart write. */
        onPrintChange(print) {
          this.uiState.draftPrintId = print.variantId;
        },

        onWrapIndividualChange(event) {
          this.uiState.wrapIndividual = event.target.checked;
        },

        /**
         * Commits print and message together, and only what actually changed — re-picking the
         * same print with an unchanged message costs no requests at all.
         */
        async confirmWrap() {
          const draftPrint = this.uiState.draftPrintId;
          if (!draftPrint) return;

          const draftMessageForLine = this.uiState.giftMessage || '';

          // Product mode writes onto the line and adds NO wrap line item: the Cart Transform
          // function creates the wrap component at checkout, and a theme-added line would double it.
          if (this.productWrapMode && this.wrapLineItem) {
            const item = this.wrapLineItem;
            await this.writeLineWrap(item, draftPrint, draftMessageForLine);
            await store.updateAttributes(
              { [config.giftWrap.optionAttributeKey]: 'individual' },
              'giftWrapOption'
            );
            store.announce(`Gift wrap added to ${item.title}`);
            this.closeWrapPanel();
            return;
          }

          const printChanged = draftPrint !== this.appliedPrintId;
          const draftMessage = this.uiState.giftMessage === null ? null : this.uiState.giftMessage;
          const currentMessage = this.cart.giftWrap.message || '';
          const messageChanged = draftMessage !== null && draftMessage !== currentMessage;

          if (printChanged) await store.setGiftWrap(draftPrint);
          if (messageChanged) await store.setGiftMessage(draftMessage);

          // Tells fulfilment how the order is to be wrapped.
          const individual = config.giftWrap.mode === 'product' || this.wrapIndividual;
          await store.updateAttributes(
            { [config.giftWrap.optionAttributeKey]: individual ? 'individual' : 'together' },
            'giftWrapOption'
          );

          const title = (this.cart.giftWrap.prints.find((p) => p.variantId === draftPrint) || {}).title || '';
          store.announce(printChanged ? `Gift box added, ${title}` : 'Gift box updated');

          this.closeWrapPanel();
        },

        /** Drops the drafts so the panel re-seeds from the cart next time it opens. */
        closeWrapPanel() {
          this.uiState.wrapPanelOpen = false;
          this.uiState.wrapLineKey = null;
          this.uiState.draftPrintId = null;
          this.uiState.giftMessage = null;
          this.uiState.wrapIndividual = null;
          this.uiState.emojiStripped = false;
        },

        /** Removal is immediate — there is nothing to confirm. */
        async removeWrap() {
          this.uiState.wrapPanelOpen = false;
          this.uiState.draftPrintId = null;
          this.uiState.giftMessage = null;
          await store.removeGiftWrap();
          await store.updateAttributes({ [config.giftWrap.optionAttributeKey]: '' }, 'giftWrapOption');
          store.announce('Gift box removed');
        },

        /**
         * Emoji are stripped as the customer types. The cart page instead waits until the
         * checkout click and then fires an alert(), which loses the caret and surprises
         * people at the worst moment.
         */
        onGiftMessageInput(event) {
          const raw = event.target.value;
          const clean = removeEmojis(raw);
          this.uiState.emojiStripped = clean !== raw;
          this.uiState.giftMessage = clean;
          // Rewrite the field only when something was actually removed, so the caret is
          // preserved during ordinary typing.
          if (clean !== raw) event.target.value = clean;
        },

        /** Theme's own star markup expects these custom properties (component-rating.css). */
        ratingStyle(rec) {
          const decimal = rec.ratingValue % 1;
          let ratingDecimal = 0;
          if (decimal >= 0.3 && decimal <= 0.7) ratingDecimal = 0.5;
          else if (decimal > 0.7) ratingDecimal = 1;
          return {
            '--rating': String(Math.floor(rec.ratingValue)),
            '--rating-max': '5',
            '--rating-decimal': String(ratingDecimal),
            '--letter-spacing': '0.3',
            '--font-size': '1.2',
          };
        },

        /**
         * Option selection is per option NAME, not per variant. A product with Color AND Ring
         * Size needs both chosen; treating each variant as one swatch produced five identical
         * colour circles for a ring in five sizes.
         *
         * Defaults to the first available variant's values so ADD works untouched.
         */
        chosenOption(rec, optionName) {
          const chosen = this.uiState.recOptionChoice[rec.productId];
          if (chosen && chosen[optionName] !== undefined) return chosen[optionName];
          const fallback = rec.variants.find((v) => v.available) || rec.variants[0];
          return fallback ? fallback.optionValues[optionName] : '';
        },

        chooseOption(rec, optionName, value) {
          if (!value) return;
          const current = { ...(this.uiState.recOptionChoice[rec.productId] || {}) };
          rec.options.forEach((o) => {
            if (current[o.name] === undefined) current[o.name] = this.chosenOption(rec, o.name);
          });
          current[optionName] = value;

          // Changing one option can land on a sold-out combination; move the OTHER options to
          // values that do exist rather than leaving ADD dead with no explanation.
          const matches = (v) => Object.keys(current).every((k) => v.optionValues[k] === current[k]);
          if (!rec.variants.some((v) => matches(v) && v.available)) {
            const rescue = rec.variants.find((v) => v.available && v.optionValues[optionName] === value);
            if (rescue) rec.options.forEach((o) => { current[o.name] = rescue.optionValues[o.name]; });
          }

          this.uiState.recOptionChoice[rec.productId] = current;
        },

        /** Whether any variant with this option value can actually be bought. */
        isOptionValueAvailable(rec, optionName, value) {
          return rec.variants.some((v) => v.optionValues[optionName] === value && v.available);
        },

        swatchImageFor(rec, optionName, value) {
          const match = rec.variants.find((v) => v.optionValues[optionName] === value && v.swatch);
          return match ? match.swatch : null;
        },

        isSwatchesExpanded(rec) {
          return !!this.uiState.recSwatchesExpanded[rec.productId];
        },

        expandSwatches(rec) {
          this.uiState.recSwatchesExpanded[rec.productId] = true;
        },

        /** The variant matching every currently chosen option value. */
        selectedVariantFor(rec) {
          const wanted = {};
          rec.options.forEach((o) => { wanted[o.name] = this.chosenOption(rec, o.name); });
          const match = rec.variants.find((v) =>
            Object.keys(wanted).every((k) => v.optionValues[k] === wanted[k])
          );
          if (match) return match.available ? match.id : null;
          // No options at all: single-variant product.
          if (!rec.options.length) {
            const only = rec.variants.find((v) => v.available) || rec.variants[0];
            return only && only.available ? only.id : null;
          }
          return null;
        },

        /** Card image follows the chosen swatch, like the product cards on the storefront. */
        recImage(rec) {
          const wanted = {};
          rec.options.forEach((o) => { wanted[o.name] = this.chosenOption(rec, o.name); });
          const match = rec.variants.find((v) =>
            Object.keys(wanted).every((k) => v.optionValues[k] === wanted[k])
          );
          return (match && match.image) || rec.image;
        },

        onRecommendationClick(rec) {
          const api = window.BrightonDrawerRecommendations;
          if (api) api.trackClick(this.uiState.recommendationsResultId, rec.productId);
          document.dispatchEvent(new CustomEvent('brighton:cart:recommendation-click', {
            detail: {
              product: rec,
              index: this.visibleRecommendations.indexOf(rec),
              listId: this.recommendationsListId,
              listName: this.recommendationsTitle,
            },
          }));
        },

        async addRecommendation(rec) {
          const variantId = this.selectedVariantFor(rec);
          if (!variantId) return;

          const actionKey = `rec-${rec.productId}`;
          // skipOpen: the drawer is already open, and an add from inside it must not re-trigger
          // the open sequence (focus move, Klarna re-init).
          await store.withSkipOpen(() => store.addItems([{ id: Number(variantId), quantity: 1 }], actionKey));

          const api = window.BrightonDrawerRecommendations;
          if (api) api.trackAddToCart(this.uiState.recommendationsResultId, rec.productId);

          // Pushed after the add so a failed one is not reported as a click-through.
          if (this.config.abTracking) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'cart_addon_nosto_click',
              product_id: String(rec.productId),
              variant_id: String(variantId),
              product_title: rec.title,
              list_id: this.recommendationsListId,
            });
          }

          store.announce(`${rec.title} added to your cart`);
        },

        /**
         * Adds a tag-triggered add-on.
         *
         * The GWP eligibility properties come from Liquid and are passed through untouched: they
         * must match what the cart page's hidden inputs submit exactly, or Shopify treats an
         * add-on added here as a different line from the same one added there.
         */
        showQuickViewButton(addOn) {
          return !!(this.config.addOns.quickView && this.config.addOns.quickView.enabled && addOn.quickView);
        },

        openQuickView(addOn, event) {
          quickViewReturnFocus = event && event.currentTarget ? event.currentTarget : null;
          this.uiState.quickViewAddOn = addOn;
          this.uiState.quickViewQuantity = 1;
          nextTick(() => {
            if (this.$refs.quickViewClose) this.$refs.quickViewClose.focus();
            this.syncQuickViewWishlist();
          });
        },

        closeQuickView() {
          this.uiState.quickViewAddOn = null;
          const target = quickViewReturnFocus;
          quickViewReturnFocus = null;
          if (target && typeof target.focus === 'function') nextTick(() => target.focus());
        },

        setQuickViewQuantity(value) {
          this.uiState.quickViewQuantity = Math.max(1, Math.min(99, Number(value) || 1));
        },

        /**
         * Renders Swym's own declarative button and asks Swym to bind it, mirroring
         * global.js:866-880. Doing it this way — rather than calling addToList ourselves — keeps the
         * heart on the SAME favourites list as the product page, with Swym owning the filled state.
         */
        syncQuickViewWishlist() {
          if (!this.config.addOns.quickView.showWishlist) return;
          const wrap = this.$refs.quickViewWishlist;
          const product = this.quickView;
          const selected = this.quickViewSelected;
          if (!wrap || !product || !selected) return;

          let button = wrap.querySelector('a.swym-button');
          if (!button) {
            button = document.createElement('a');
            button.className = 'swym-button swym-add-to-wishlist-view-product';
            button.setAttribute('data-swaction', 'addToWishlist');
            button.setAttribute('data-with-epi', 'true');
            // Whitespace so base.css's a:empty rule can't hide it, as main-product.liquid does.
            button.appendChild(document.createTextNode(' '));
            wrap.appendChild(button);
          }

          const base = new URL(product.url, window.location.origin);
          base.searchParams.set('variant', selected.id);
          button.setAttribute('data-product-id', String(product.productId));
          button.setAttribute('data-variant-id', String(selected.id));
          button.setAttribute('data-product-url', base.href);

          if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
            window._swat.initializeActionButtons('body');
          }
        },

        async addQuickView() {
          const product = this.quickView;
          const variantId = this.selectedVariantFor(product);
          if (!product || !variantId) return;

          const line = { id: Number(variantId), quantity: this.quickViewQuantity };
          // Same GWP eligibility properties as a direct add-on add, or Shopify treats the two as
          // different lines and refuses to merge them.
          if (product.properties && Object.keys(product.properties).length) {
            line.properties = product.properties;
          }

          await store.withSkipOpen(() => store.addItems([line], 'quick-view'));
          store.announce(`${product.title} added to your cart`);
          this.closeQuickView();
        },

        async addAddOn(addOn) {
          if (!addOn.available) return;

          const actionKey = `addon-${addOn.productId}`;
          const line = { id: Number(addOn.variantId), quantity: 1 };
          if (addOn.properties && Object.keys(addOn.properties).length) {
            line.properties = addOn.properties;
          }

          // skipOpen: the drawer is already open; adding from inside it must not re-run the open
          // sequence (focus move, Klarna re-init).
          await store.withSkipOpen(() => store.addItems([line], actionKey));
          store.announce(`${addOn.title} added to your cart`);
        },


        /** Scrolls by one card width; the list is a plain scroll container, not a JS slider. */
        scrollRecommendations(direction) {
          const list = this.$refs.carousel;
          if (!list) return;
          const card = list.querySelector('.cart-drawer__rec');
          const step = card ? card.getBoundingClientRect().width + 12 : list.clientWidth;
          list.scrollBy({ left: step * direction, behavior: 'smooth' });
        },

        scrollAddOns(direction) {
          const list = this.$refs.addOnCarousel;
          if (!list) return;
          const card = list.querySelector('.cart-drawer__add-on');
          const step = card ? card.getBoundingClientRect().width + 12 : list.clientWidth;
          list.scrollBy({ left: step * direction, behavior: 'smooth' });
        },

        /** Save for Later is offered per line, unless it's a gift, wrap, or already saved. */
        canSaveForLater(item) {
          if (!this.config.saveForLater.enabled) return false;
          if (!item.flags.savableForLater) return false;
          return this.savedVariantIds.indexOf(item.variantId) === -1;
        },

        isSavingLine(item) {
          return !!this.uiState.savingLines[item.key];
        },

        /**
         * Adds to the wishlist FIRST, and only removes the cart line once Swym has accepted it.
         * Removing first would risk losing the item from both places if Swym failed.
         */
        async saveForLater(item) {
          const api = window.BrightonDrawerSwym;
          if (!api || this.isSavingLine(item)) return;

          this.uiState.savingLines[item.key] = true;
          try {
            await api.save(item);
            this.uiState.savedVariantIds = this.uiState.savedVariantIds.concat([item.variantId]);
            document.dispatchEvent(new CustomEvent('brighton:cart:save-for-later', {
              detail: { productId: item.productId, variantId: item.variantId, title: item.title },
            }));
            store.announce(`${item.title} saved for later`);
            await store.removeLine(item.key);
          } catch (e) {
            store.announce('Could not save for later, please try again');
            console.warn('[cart-drawer] save for later failed', e && e.message);
          } finally {
            delete this.uiState.savingLines[item.key];
          }
        },

        /** Uploaded property values render as a link labelled with just the file name. */
        uploadFileName(value) {
          return String(value || '').split('/').pop();
        },

        /**
         * Every per-line detail as one list, whatever its source: the line's own properties, the
         * embroidery enrichment, the confirmed Design ID and the preview link.
         *
         * Unified deliberately. The gift card form stamps customer-visible `To` / `Deliver to` /
         * `Deliver on` / `Greeting` properties ALONGSIDE its internal underscore ones
         * (digital-giftcard-form.liquid:990+, populated by product-form.js), so a dedicated gift
         * card block rendered the same data twice — and with a different date format than the one
         * that reaches the order. The form's visible properties are the canonical display values,
         * so those are what get shown.
         */
        detailRows(item) {
          // Variant options lead, labelled ("Design: Default", "Amount: $25"), matching the cart
          // page. They are not also shown as a slash-joined line, which would duplicate them.
          const rows = item.options.map((o) => ({
            label: o.name,
            value: o.value,
            href: null,
            nowrap: o.isShoeWidth,
          }));

          // Wrap properties are presented by the line's own wrap block, so skip them here.
          const wrapKeys = Object.values(config.giftWrap.propertyKeys || {});
          item.visibleProperties
            .filter((p) => wrapKeys.indexOf(p.name) === -1)
            .forEach((p) => rows.push({
              label: p.name,
              value: p.isUpload ? this.uploadFileName(p.value) : p.value,
              href: p.isUpload ? p.value : null,
            }));

          // Order-mode message is a cart attribute, not a line property, so it has to be added
          // here to read as one. Product mode keeps it on the wrapped line, where the line's own
          // wrap block already shows it.
          if (item.flags.isWrap && !this.productWrapMode && this.cart.giftWrap.message) {
            rows.push({ label: 'Message', value: this.cart.giftWrap.message, href: null });
          }

          const entry = this.embroideryState[item.key];
          if (entry) {
            (entry.rows || []).forEach((r) => rows.push({ label: r.label, value: r.value, href: null }));
            if (entry.designId) {
              rows.push({ label: 'Design ID', value: entry.designId, href: null, copyable: true });
            }
          }
          if (item.embroidery && item.embroidery.previewUrl) {
            // Not an <a href>: the preview is a direct image URL, and a plain link lets the
            // browser's content-disposition decide — which downloaded the file instead of showing
            // it. Opens an in-drawer lightbox instead, like the cart page's pplr lightbox.
            rows.push({
              label: 'Embroidery Preview',
              value: 'View',
              preview: true,
              url: item.embroidery.previewUrl,
            });
          }
          return rows;
        },

        openPreview(row, item) {
          this.uiState.preview = { url: sizedImageUrl(row.url, PREVIEW_WIDTH), title: item.title };
          nextTick(() => {
            const close = host.querySelector('.cart-drawer__lightbox-close');
            if (close) close.focus();
          });
        },

        closePreview() {
          this.uiState.preview = null;
        },

        /** Collapsed shows only the first row, so a gift card or embroidery line stays compact. */
        shownDetailRows(item) {
          const rows = this.detailRows(item);
          return this.isDetailsExpanded(item) ? rows : rows.slice(0, DETAIL_ROWS_COLLAPSED);
        },

        isDetailsExpanded(item) {
          return !!this.uiState.expandedDetails[item.key];
        },

        toggleDetails(item) {
          this.uiState.expandedDetails[item.key] = !this.uiState.expandedDetails[item.key];
        },

        copyValue(item, row) {
          if (!row.value || !navigator.clipboard) return;
          navigator.clipboard.writeText(row.value).then(() => {
            this.copiedDesignIds[item.key] = true;
            store.announce(`${row.label} ${row.value} copied`);
            setTimeout(() => { this.copiedDesignIds[item.key] = false; }, 2000);
          });
        },

        lineStatus(item) {
          return store.lineStatus(item.key);
        },
        isLineBusy(item) {
          return store.isLinePending(item.key);
        },
        lineError(item) {
          return store.lineError(item.key);
        },
        lineStatusClass(item) {
          const status = store.lineStatus(item.key);
          return status ? `is-${status}` : '';
        },
        isActionPending(name) {
          return store.isActionPending(name);
        },
        increase(item) {
          store.updateQuantity(item.key, item.quantity + 1);
        },
        decrease(item) {
          store.updateQuantity(item.key, item.quantity - 1);
        },
        remove(item) {
          store.removeLine(item.key);
          store.announce(`${item.title} removed from your cart`);
        },
        onNoteChange(event) {
          store.updateNote(event.target.value);
        },
        checkout() {
          if (window.LoopOnstore?.isActive?.()) return;
          document.dispatchEvent(
            new CustomEvent('brighton:cart:checkout', { detail: { cart: this.cart } })
          );
          // Marks the session as drawer-initiated. Not a dl_ event: Elevar owns that namespace
          // and already emits begin_checkout.
          if (this.config.abTracking) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'cart_checkout_initiated', checkout_source: 'cart_drawer' });
          }
          window.location.href = this.config.routes.checkout;
        },
        close() {
          store.closeDrawer();
        },
      },
    });

    const vm = app.mount(mount);

    /* ---------------------------------------------------------------------- */
    /* Custom element: the Shopify.actions.openCart contract                   */
    /* ---------------------------------------------------------------------- */

    let lastFocused = null;

    const focusableSelector = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'textarea:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    // Topmost dialog first. All three are siblings, so Tab must be trapped in whichever is on top.
    const DIALOG_STACK = [
      '.cart-drawer__lightbox',
      '.cart-drawer__quick-view',
      '.cart-drawer__wrap-drawer',
      '.cart-drawer__panel',
    ];

    const topDialog = () => {
      for (const selector of DIALOG_STACK) {
        const el = host.querySelector(selector);
        if (el) return el;
      }
      return null;
    };

    const trapFocus = (event) => {
      if (!store.state.open || event.key !== 'Tab') return;

      const container = topDialog();
      if (!container) return;

      const focusable = [...container.querySelectorAll(focusableSelector)].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Focus sitting outside the topmost dialog (the lightbox just opened) belongs back inside it.
      if (!container.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onKeydown = (event) => {
      // Escape unwinds the stack one level at a time, never closing the cart out from under an
      // overlay the customer is actually looking at.
      if (event.key === 'Escape') {
        if (vm && vm.preview) {
          vm.closePreview();
          return;
        }
        if (vm && vm.quickView) {
          vm.closeQuickView();
          return;
        }
        if (vm && vm.wrapPanelOpen) {
          vm.cancelWrapPanel();
          return;
        }
        if (store.state.open) {
          store.closeDrawer();
          return;
        }
      }
      trapFocus(event);
    };

    document.addEventListener('keydown', onKeydown);

    /**
     * The header cart bubble lives outside the Vue root, so it is synced imperatively
     * rather than by re-rendering the header section. Rebuy does the same thing with
     * updateCartCount / updateCartSubtotal.
     */
    const syncCartBubble = () => {
      const count = store.state.cart.visibleItemCount;
      const bubbleHost = document.getElementById('cart-icon-bubble');
      if (!bubbleHost) return;
      const bubble = bubbleHost.querySelector('.cart-count-bubble');
      if (!bubble) return;
      const visible = bubble.querySelector('[aria-hidden="true"]');
      if (visible) visible.textContent = count < 100 ? String(count) : '';
      const hidden = bubble.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = `${count} item${count === 1 ? '' : 's'}`;
      bubble.classList.toggle('hidden', count === 0);
    };

    /**
     * Klarna renders into a shadow root and reads the amount from data-purchase-amount, so
     * the attribute is rewritten and refresh() called whenever the subtotal changes.
     */
    let klarnaPlacement = null;
    let klarnaAmount = null;

    /**
     * Creates the placement inside a shadow root, deliberately.
     *
     * The Klarna app extension collects placements with
     * `Array.from(document.getElementsByTagName('klarna-placement'))` and runs a
     * MutationObserver that force-writes `data-purchase-amount` on every one of them to the
     * current PAGE amount. On a product page that silently rewrote the drawer's cart total to
     * the product's price — a $232 cart showed Klarna terms for a $42 item. Its code has no
     * skip/ignore/exclude opt-out of any kind.
     *
     * `document.getElementsByTagName` does not traverse shadow roots, so a placement inside
     * one is invisible to that sweep while still being upgraded by the custom element
     * definition (customElements is global). Verified: the extension no longer sees it and the
     * amount stays ours.
     */
    const createKlarnaPlacement = (slot, purchaseAmount) => {
      const shadowHost = document.createElement('div');
      shadowHost.className = 'cart-drawer__klarna-host';
      const shadow = shadowHost.attachShadow({ mode: 'open' });

      // The placement's own :host rule is auto/auto, so it needs sizing inside the shadow tree.
      const style = document.createElement('style');
      style.textContent = 'klarna-placement { display: block; width: 100%; }';
      shadow.appendChild(style);

      const placement = document.createElement('klarna-placement');
      placement.setAttribute('data-key', config.klarna.dataKey);
      placement.setAttribute('data-theme', config.klarna.theme);
      // data-locale is REQUIRED and must be full language-COUNTRY: verified that both omitting
      // it and passing bare "en" make the placement render at zero height.
      placement.setAttribute('data-locale', config.klarna.locale);
      // Set BEFORE connecting: Klarna reads its attributes when the element upgrades, and
      // appending first made it warn that the required amount was missing.
      placement.setAttribute('data-purchase-amount', String(purchaseAmount));
      shadow.appendChild(placement);

      slot.appendChild(shadowHost);
      return placement;
    };

    const syncKlarna = () => nextTick(() => {
      if (!config.klarna.enabled) return;
      const slot = host.querySelector('.cart-drawer__klarna');
      if (!slot) return;

      loadKlarna(config.klarna.clientId)
        .then(() => {
          const amount = store.state.cart.totalPrice;
          const isNew = !klarnaPlacement || !klarnaPlacement.isConnected;

          if (isNew) {
            slot.innerHTML = '';
            klarnaPlacement = createKlarnaPlacement(slot, amount);
          } else {
            if (amount === klarnaAmount) return;
            klarnaPlacement.setAttribute('data-purchase-amount', String(amount));
          }

          klarnaAmount = amount;
          if (window.Klarna && window.Klarna.OnsiteMessaging && window.Klarna.OnsiteMessaging.refresh) {
            window.Klarna.OnsiteMessaging.refresh();
          }
        })
        .catch((e) => console.warn('[cart-drawer] Klarna unavailable', e.message));
    });

    if (!customElements.get('cart-drawer')) {
      customElements.define(
        'cart-drawer',
        class CartDrawer extends HTMLElement {
          /** Called by Shopify.actions.openCart(). */
          open() {
            store.openDrawer();
          }

          close() {
            store.closeDrawer();
          }

          /** Teardown, for switching cohorts without a reload. */
          destroy() {
            document.removeEventListener('keydown', onKeydown);
            app.unmount();
          }
        }
      );
    }

    /**
     * All the DOM-side work of opening hangs off the store's event, not off .open(), because
     * the drawer can be opened from several places — Shopify.actions.openCart(), the header
     * trigger, add-to-cart, ?viewcart=true, the overlay closing and reopening. Centralising
     * here means focus, the body lock and Klarna init cannot be skipped by one path.
     */
    document.addEventListener('brighton:cart:open', () => {
      document.body.classList.add('cart-drawer-open');
      // Wait for the panel to be visible before moving focus into it.
      requestAnimationFrame(() => {
        const closeButton = host.querySelector('.cart-drawer__close');
        if (closeButton) closeButton.focus();
        // Measure only once the panel has its real width — a closed panel can report zero.
        fitThresholdMessages();
        measureTrailingGap();
      });
      syncKlarna();
    });

    // Panel width tracks the viewport on mobile, so a rotation or resize needs a re-fit.
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        fitThresholdMessages();
        measureTrailingGap();
      }, 150);
    });

    // Keep the +N count in step with the CSS when the viewport crosses the breakpoint.
    narrowSwatches.addEventListener('change', () => {
      vm.uiState.swatchLimit = currentSwatchLimit();
    });

    document.addEventListener('brighton:cart:close', () => {
      document.body.classList.remove('cart-drawer-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      const active = document.activeElement;
      if (active && host.contains(active) && typeof active.blur === 'function') active.blur();
    });

    // Remember what to return focus to, before the drawer steals it.
    document.addEventListener(
      'click',
      (event) => {
        if (host.contains(event.target)) return;
        lastFocused = event.target;
      },
      true
    );

    ['brighton:cart:ready', 'brighton:cart:line-item-increase', 'brighton:cart:line-item-decrease',
      'brighton:cart:line-item-removed', 'brighton:cart:add', 'brighton:cart:reconciled',
    ].forEach((name) => {
      document.addEventListener(name, () => {
        syncCartBubble();
        syncKlarna();
      });
    });

    /**
     * Recommendation impressions fire when the carousel is genuinely on screen. The drawer body
     * scrolls, so a rendered carousel is frequently below the fold — counting it as seen on
     * render would inflate view_item_list badly.
     */
    if ('IntersectionObserver' in window) {
      const carouselObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) vm.announceImpression();
        },
        { root: host.querySelector('.cart-drawer__body'), threshold: 0.5 }
      );

      // The carousel is created and destroyed by Vue, so re-observe whenever it appears.
      const watchForCarousel = new MutationObserver(() => {
        const carousel = host.querySelector('.cart-drawer__carousel');
        if (carousel && carousel !== watchForCarousel._seen) {
          watchForCarousel._seen = carousel;
          carouselObserver.observe(carousel);
        }
      });
      watchForCarousel.observe(host, { childList: true, subtree: true });
    }

    syncCartBubble();
    window.BrightonCartDrawer = { store, host, vm, syncKlarna, syncCartBubble };

    // Everything is wired — now fire lifecycle events and honour ?viewcart=true.
    store.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
